import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useReadSpeed — Speed Memory hook.
 * Remembers the child's chosen TTS reading speed per profile in localStorage.
 * Also exposes karaoke word-level highlighting utilities shared across
 * Summer Slide, SEL, and Kiddom Math lesson renderers.
 */
export const SPEED_PRESETS = {
  slow:   { voice: 0.75, wordBase: 350, wordCharBonus: 45, label: 'Slow',   icon: 'turtle' },
  normal: { voice: 0.95, wordBase: 230, wordCharBonus: 30, label: 'Normal', icon: 'rabbit' },
  fast:   { voice: 1.15, wordBase: 170, wordCharBonus: 20, label: 'Fast',   icon: 'rabbit-fast' },
};

const STORAGE_PREFIX = 'zazu.readSpeed.';

const readStored = (childId) => {
  if (!childId) return 'normal';
  try {
    const v = window.localStorage.getItem(`${STORAGE_PREFIX}${childId}`);
    if (v && SPEED_PRESETS[v]) return v;
  } catch {
    /* ignore */
  }
  return 'normal';
};

export function useReadSpeed(childId) {
  const [readSpeed, setReadSpeedState] = useState(() => readStored(childId));

  // If the child changes (parent switches profile), load their remembered speed.
  useEffect(() => {
    setReadSpeedState(readStored(childId));
  }, [childId]);

  const setReadSpeed = useCallback(
    (next) => {
      const value = typeof next === 'function' ? next(readSpeed) : next;
      const safe = SPEED_PRESETS[value] ? value : 'normal';
      setReadSpeedState(safe);
      if (childId) {
        try {
          window.localStorage.setItem(`${STORAGE_PREFIX}${childId}`, safe);
        } catch {
          /* ignore */
        }
      }
    },
    [childId, readSpeed]
  );

  const cycleReadSpeed = useCallback(() => {
    setReadSpeed((cur) => (cur === 'slow' ? 'normal' : cur === 'normal' ? 'fast' : 'slow'));
  }, [setReadSpeed]);

  return { readSpeed, setReadSpeed, cycleReadSpeed, preset: SPEED_PRESETS[readSpeed] };
}

// ------------------------------------------------------------------
// Karaoke helpers — shared between Summer Slide, SEL, and Kiddom.
// ------------------------------------------------------------------

export const splitSentences = (text) => {
  if (!text) return [];
  return String(text)
    .split(/(?<=[.!?])\s+/g)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const splitWords = (sentence) => {
  if (!sentence) return [];
  return sentence.match(/\S+\s*/g) || [];
};

/**
 * useKaraoke — orchestrates sentence-level and word-level highlighting
 * for a passage read aloud via `speakChunks`.
 *
 * Returns:
 *   highlightedSentence, highlightedWord — current indexes (or -1)
 *   playPassage(passage, opts) — start reading (with title prefix optional)
 *   stopPassage() — halt TTS and clear all highlights
 */
export function useKaraoke({ speakChunks, stopStory, isReading, readSpeed }) {
  const [highlightedSentence, setHighlightedSentence] = useState(-1);
  const [highlightedWord, setHighlightedWord] = useState(-1);
  const wordTimerRef = useRef(null);

  const clearWordHighlighting = useCallback(() => {
    if (wordTimerRef.current) {
      wordTimerRef.current.forEach((id) => clearTimeout(id));
      wordTimerRef.current = null;
    }
    setHighlightedWord(-1);
  }, []);

  const startWordHighlighting = useCallback(
    (sentence) => {
      clearWordHighlighting();
      const words = splitWords(sentence).map((w) => w.trim()).filter(Boolean);
      if (!words.length) return;
      const preset = SPEED_PRESETS[readSpeed] || SPEED_PRESETS.normal;
      const timers = [];
      let elapsed = 0;
      words.forEach((w, i) => {
        const dur = Math.max(
          preset.wordBase - 50,
          preset.wordBase + Math.max(0, w.length - 4) * preset.wordCharBonus
        );
        const t = setTimeout(() => setHighlightedWord(i), elapsed);
        timers.push(t);
        elapsed += dur;
      });
      timers.push(setTimeout(() => setHighlightedWord(-1), elapsed + 200));
      wordTimerRef.current = timers;
    },
    [readSpeed, clearWordHighlighting]
  );

  const playPassage = useCallback(
    (passage, { title } = {}) => {
      const sentences = splitSentences(passage || '');
      const chunks = title ? [`${title}.`, ...sentences] : sentences;
      const offset = title ? -1 : 0;
      const preset = SPEED_PRESETS[readSpeed] || SPEED_PRESETS.normal;
      speakChunks(
        chunks,
        (i) => {
          if (i < 0) {
            setHighlightedSentence(-1);
            clearWordHighlighting();
            return;
          }
          const sentenceIdx = i + offset;
          setHighlightedSentence(sentenceIdx);
          if (sentenceIdx >= 0) {
            startWordHighlighting(sentences[sentenceIdx] || '');
          } else {
            clearWordHighlighting();
          }
        },
        { speed: preset.voice }
      );
    },
    [readSpeed, speakChunks, startWordHighlighting, clearWordHighlighting]
  );

  const stopPassage = useCallback(() => {
    stopStory?.();
    setHighlightedSentence(-1);
    clearWordHighlighting();
  }, [stopStory, clearWordHighlighting]);

  // Cleanup on unmount
  useEffect(() => () => clearWordHighlighting(), [clearWordHighlighting]);

  return {
    highlightedSentence,
    highlightedWord,
    playPassage,
    stopPassage,
    isReading,
  };
}
